/**
 * Typed client for /admin-support/* — backs the Customer Service screen.
 *
 * Endpoints return raw shapes (no `{ success, data }` envelope on this
 * surface), so we hit `client` directly and read `r.data`. Keep this
 * file the single source of truth for admin-support response shapes;
 * the screen + React Query hooks both consume the types from here.
 */
import { client } from './client';

// ─── Shared types ─────────────────────────────────────────────────────────

export type DeviceStatus =
  | 'installed'
  | 'uninstalled'
  | 'pending'
  | 'inactive'
  | 'active'
  | 'rejected';

export type PublisherStatus = 'active' | 'pending' | 'inactive' | 'rejected';

export type Platform = 'windows' | 'mac' | 'linux';

export type Source = 'desktop' | 'sdk' | 'browser' | 'mobile' | null;

export interface PublisherSummary {
  _id: string;
  name?: string;
  email: string;
  companyName?: string;
  role?: string;
  status?: PublisherStatus;
  publisherKey?: string;
  publisherNumber?: number;
  createdAt?: string;
  installs?: number;
  live?: number;
  liveTime?: number;
  earnings?: number;
}

export interface DeviceSummary {
  _id: string;
  userKey: string;
  device?: string;
  operatingSystem?: string;
  platform?: Platform | null;
  status?: DeviceStatus;
  balance?: number;
  liveTime?: number;
  currencyEarned?: number;
  currencySpent?: number;
  publisherId?: { _id: string; name?: string; email?: string; companyName?: string };
  publisherKey?: string;
  source?: Source;
  installedAt?: string;
  uninstalledAt?: string | null;
}

export interface OverviewResponse {
  publishers: { total: number; active: number; pending: number };
  devices: { total: number; installed: number };
  activeSessions: number;
  platforms: Record<string, number>;
  sources: Record<string, number>;
  installTrend: Array<{ _id: string; count: number }>;
}

export interface PagedList<T> {
  data: T[];
  total: number;
  page: number;
  pages: number;
}

export interface SearchResponse {
  publishers?: PagedList<PublisherSummary>;
  devices?: PagedList<DeviceSummary>;
}

export interface SessionDetail {
  _id: string;
  startAt?: string;
  endAt?: string | null;
  duration?: number;
  platform?: Platform | null;
  source?: Source;
  minerStats?: { hashrate?: number };
}

export interface DeviceDetailResponse {
  device: DeviceSummary & {
    balanceDisplay?: number;
    appId?: { _id: string; name?: string; appKey?: string };
  };
  sessions: SessionDetail[];
  activeSession: SessionDetail | null;
}

export interface AppSummary {
  _id: string;
  name?: string;
  appKey: string;
  installs?: number;
  uninstalls?: number;
  live?: number;
  liveTime?: number;
  earnings?: number;
  createdAt?: string;
}

export interface WithdrawalSummary {
  _id: string;
  createdAt: string;
  status: string;
  fromAmount?: number;
  fromCurrency?: string;
  toAmount?: number;
  outCurrency?: string;
  exchangeProvider?: string;
}

export interface PublisherDetailResponse {
  publisher: PublisherSummary & { payoutCurrency?: string };
  apps: AppSummary[];
  balance: number;
  devices: { installed: number; total: number };
  activeSessions: number;
  recentWithdrawals: WithdrawalSummary[];
}

export interface AdjustBalanceResponse {
  success: boolean;
  userKey: string;
  newBalance: number;
}

// ─── API calls ────────────────────────────────────────────────────────────

export async function fetchSupportOverview(): Promise<OverviewResponse> {
  const r = await client.get<OverviewResponse>('/admin-support/overview');
  return r.data;
}

export interface SearchParams {
  q: string;
  type?: 'all' | 'publishers' | 'devices';
  page?: number;
  limit?: number;
}

export async function searchSupport(params: SearchParams): Promise<SearchResponse> {
  const r = await client.get<SearchResponse>('/admin-support/search', { params });
  return r.data;
}

export async function fetchDeviceDetail(id: string): Promise<DeviceDetailResponse> {
  const r = await client.get<DeviceDetailResponse>(`/admin-support/device/${id}`);
  return r.data;
}

export async function fetchPublisherDetail(id: string): Promise<PublisherDetailResponse> {
  const r = await client.get<PublisherDetailResponse>(
    `/admin-support/publisher/${id}`
  );
  return r.data;
}

export interface AdjustBalanceInput {
  deviceId: string;
  amount: number;
  reason: string;
}

export async function adjustBalance(
  input: AdjustBalanceInput
): Promise<AdjustBalanceResponse> {
  const r = await client.post<AdjustBalanceResponse>(
    `/admin-support/device/${input.deviceId}/adjust-balance`,
    { amount: input.amount, reason: input.reason }
  );
  return r.data;
}

// ── Act as a creator / manual KYC tier ─────────────────────────────────
// POST /login-as (nurev-api #142): a 2-hour token for a non-admin account,
// recorded in the audit log. The console opens app.nurev.io/impersonate with
// it in the URL fragment (never sent to a server).
export interface LoginAsResponse {
  token: string;
  user: { _id: string; email: string; role: string };
  expiresInMinutes: number;
}
export async function loginAs(userId: string): Promise<LoginAsResponse> {
  const r = await client.post<LoginAsResponse>('/login-as', { _id: userId });
  return r.data;
}

// POST /admin-support/publisher/:id/kyc-tier (nurev-api #143): manual tier
// override with a recorded reason — the stopgap while Sumsub isn't live.
export type OverridableTier = 'pending' | 'tier1_lite_verified' | 'tier2_full_verified';
export async function setKycTier(
  id: string,
  tier: OverridableTier,
  reason: string
): Promise<{ kycTier: string; previousTier: string }> {
  const r = await client.post(`/admin-support/publisher/${id}/kyc-tier`, { tier, reason });
  return r.data;
}
